import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ActorProfileMaster, DashboardConfigWidget, DashboardState, MealBreakWidgetConfig } from "../../../types";
import { fetchDashboardWidgetConfig } from "./api";

const DEFAULT_MEAL_BREAK_WIDGET_CONFIG: MealBreakWidgetConfig = {
    visible: false,
    workflowCode: "",
    entryId: "",
};

const initialState: DashboardState = {
    viewAllEmployees: [],
    selectedSchedulesForViewDetails: [],
    selectedScheduleForViewShiftDetails: null,
    dashboardConfigWidgets: [],
    dashboardConfigCustomLinks: [],
    mealBreakWidgetConfig: DEFAULT_MEAL_BREAK_WIDGET_CONFIG,
    dashboardConfigLoading: false,
    dashboardConfigLoadedForUserId: null,
    dashboardConfigError: null,
};

const getMealBreakWidgetConfigFromWidgets = (widgets: DashboardConfigWidget[] = []): MealBreakWidgetConfig => {
    const mealBreakWidget = widgets.find(
        (widget) => widget.widget_code === "skip_meal_break" || widget.widget_code === "meal_break"
    );

    if (!mealBreakWidget) {
        return DEFAULT_MEAL_BREAK_WIDGET_CONFIG;
    }

    return {
        visible: Boolean(mealBreakWidget.visible),
        workflowCode: mealBreakWidget.config?.workflow_code || "",
        entryId: mealBreakWidget.config?.entry_id ? String(mealBreakWidget.config.entry_id) : "",
    };
};

export const DashboardSlice = createSlice({
    name: "dashboard",
    initialState: initialState,
    reducers: {
        setViewAllEmployees: (state, action: PayloadAction<ActorProfileMaster[]>) => {
            state.viewAllEmployees = action.payload;
        },

        clearViewAllEmployees: (state) => {
            state.viewAllEmployees = [];
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchDashboardWidgetConfig.pending, (state) => {
                state.dashboardConfigLoading = true;
                state.dashboardConfigError = null;
            })
            .addCase(fetchDashboardWidgetConfig.fulfilled, (state, action) => {
                const { widgets: dashboardWidgets, customLinks: dashboardCustomLinks, userId } = action.payload;

                state.dashboardConfigLoading = false;
                state.dashboardConfigWidgets = dashboardWidgets || [];
                state.dashboardConfigCustomLinks = dashboardCustomLinks || [];
                state.mealBreakWidgetConfig = getMealBreakWidgetConfigFromWidgets(dashboardWidgets);
                state.dashboardConfigLoadedForUserId = userId;
                state.dashboardConfigError = null;
            })
            .addCase(fetchDashboardWidgetConfig.rejected, (state, action) => {
                const rejectedUserId = action.payload?.userId ?? null;
                const isSameUserConfig = state.dashboardConfigLoadedForUserId === rejectedUserId;

                state.dashboardConfigLoading = false;
                state.dashboardConfigLoadedForUserId = rejectedUserId;

                if (!isSameUserConfig) {
                    state.dashboardConfigWidgets = [];
                    state.dashboardConfigCustomLinks = [];
                    state.mealBreakWidgetConfig = DEFAULT_MEAL_BREAK_WIDGET_CONFIG;
                }

                state.dashboardConfigError =
                    action.payload?.message || action.error.message || "Failed to load dashboard config";
            });
    },
});

export const { setViewAllEmployees, clearViewAllEmployees } = DashboardSlice.actions;
export default DashboardSlice.reducer;
